import { skipToken } from "@tanstack/react-query";
import { CONTRACTOR_TABLE_ROUTE } from "@remi/constants";
import { ContractorTier } from "@remi/enums/contractor-tier";
import { ContractorType } from "@remi/enums/contractor-type";
import { PermittingEntity } from "@remi/enums/permitting-entity";
import { createIdFromName, getLinktoQbVendor } from "@remi/formatting";
import { remiLogger } from "@remi/logging";
import { RemiLink } from "@remi/next-ui";
import { can } from "@remi/permissions";
import { ContractorStatus } from "@remi/types/contractorStatus";

import { api } from "~/utils/api";
import { displayToast } from "~/components/ui/sonner";
import { useUserProfile } from "~/contexts/UserProfileContext";
import useNavigation from "./useNavigation";

export const useContractorProfileCreation = () => {
  const { navigate } = useNavigation();
  const { contractor: contractorRoute } = api.useUtils();
  const mutation = api.contractor.createContractorProfile.useMutation({
    onMutate({ contractorName }) {
      const contractorId = createIdFromName(contractorName);
      contractorRoute.gatherContractorProfile.setData(
        { contractorProfileId: contractorId },
        {
          organizationId: contractorId,
          createdAt: new Date(),
          status: ContractorStatus.VETTING,
          address: null,
          socialLinks: [],
          documentExemptions: [],
          organization: {
            id: contractorId,
            name: contractorName,
            contact: { name: contractorName },
            persons: [],
            phones: [],
            _count: { projects: 0 },
          },
          reviewSummary: [],
          files: [],
          owners: [],
          serviceAreas: [],
          qualifiesForPermitReimbursement: false,
          permittingEntity: PermittingEntity.CONTRACTOR,
          checkedReferences: false,
          checkedReputation: false,
          isProxyEnabled: false,
          dataExportEnabled: false,
          selfOrdersMaterialsOnRemiAccount: false,
          notes: "",
          type: ContractorType.REMI_PRO_CONTRACTOR,
          complianceEvents: [],
          totalProjects: 0,
          activeProjectsCount: 0,
          complianceManager: null,
          tier: ContractorTier.STARTER,
          tierOverride: false,
          monthlyCapacity: null,
          legalEntityName: null,
          yearsInBusiness: null,
          numberOfCrews: null,
          refContactName: null,
          refContactCompany: null,
          refContactEmail: null,
          refContactPhone: null,
          history: [],
        },
      );
    },
    onSuccess: (data) => {
      if (!data) {
        displayToast({ title: "Contractor already exists", variant: "info" });
        return;
      }
      void navigate({ pathname: `${CONTRACTOR_TABLE_ROUTE}/${data.organizationId}` });
      displayToast({
        title: "Contractor created",
        variant: "success",
      });
      data.accountingVendors.forEach((vendor) => {
        displayToast({
          title: "Accounting vendor created",
          link: (
            <RemiLink
              className="font-medium text-text-accent"
              href={getLinktoQbVendor(vendor.externalId)}
              target="_blank"
            >
              View vendor
            </RemiLink>
          ),
          variant: "success",
          description: `The bill has been successfully created in ${vendor.remiEntity} Quickbooks.`,
        });
      });
    },
    async onSettled() {
      await contractorRoute.gatherContractorProfilesForTable.invalidate();
    },
    onError(err, _input) {
      const isDuplicate = err.message.includes("already exists");
      displayToast({
        title: isDuplicate ? "A contractor with this name already exists" : "Error creating contractor",
        variant: "error",
      });
      remiLogger.error("Error creating contractor", { error: err });
      if (!isDuplicate) {
        void navigate({ pathname: CONTRACTOR_TABLE_ROUTE });
      }
    },
  });
  return { mutation };
};

export const useContractorProfile = (contractorId?: string) => {
  const { user } = useUserProfile();
  const query = api.contractor.gatherContractorProfile.useQuery(
    contractorId && can.contractorProfile.read(user, contractorId) ? { contractorProfileId: contractorId } : skipToken,
  );

  return query;
};
